import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, EMPTY, switchMap, throwError } from 'rxjs';
import { TokenService } from '../services/token.service';
import { AuthService } from '../services/auth.service';
import { AdminTokenService } from '../services/admin-token.service';
import { AdminAuthService } from '../services/admin-auth.service';
import { CommitteeTokenService } from '../services/committee-token.service';
import { NotifyService } from '../../shared/services/notify.service';
import { getDefaultOwnerRoute } from '../guards/permission.guard';

/** ตรวจว่า URL เป็น admin API มั้ย — ใช้ prefix matching */
function isAdminApi(url: string): boolean {
  return url.includes('/api/auth/admin/') || url.includes('/api/admin/');
}

/** committee API ที่ต้องแนบ session token (ยกเว้น /auth/* ที่เป็น public) */
function isCommitteeApi(url: string): boolean {
  return url.includes('/api/committee/') && !url.includes('/api/committee/auth/');
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const token        = inject(TokenService);
  const authSvc      = inject(AuthService);
  const adminToken   = inject(AdminTokenService);
  const adminAuthSvc = inject(AdminAuthService);
  const committeeToken = inject(CommitteeTokenService);
  const router       = inject(Router);
  const notify       = inject(NotifyService);
  const platformId   = inject(PLATFORM_ID);
  const isBrowser    = isPlatformBrowser(platformId);

  const useAdmin = isAdminApi(req.url);
  const useCommittee = isCommitteeApi(req.url);
  const accessToken = useAdmin ? adminToken.getAccessToken()
    : useCommittee ? committeeToken.getAccessToken()
    : token.getAccessToken();

  const authReq = accessToken
    ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: unknown) => {
      // ── 403 CHANGE_PASSWORD_REQUIRED ─────────────────────────
      // user ที่ยังไม่ได้เปลี่ยน password — refresh /api/auth/me เพื่อให้ App component
      // ตรวจ flag changePasswordFirst แล้วเด้ง popup. ส่ง error ต่อให้ caller จัดการเอง
      if (err instanceof HttpErrorResponse && err.status === 403
          && err.error?.error_code === 'CHANGE_PASSWORD_REQUIRED' && !useAdmin) {
        if (isBrowser) authSvc.refreshFromToken();
        return throwError(() => err);
      }

      // ── 403 PERMISSION_DENIED (owner API) ─────────────────────
      // สิทธิ์ถูกถอด/ไม่พอ ระหว่างที่ user อยู่ในหน้านั้น — สด/sync user แล้ว
      // เด้งไปหน้าแรกที่ user เข้าได้ + toast แทนการแสดง error 403 บนหน้าจอ
      if (err instanceof HttpErrorResponse && err.status === 403
          && err.error?.error_code === 'PERMISSION_DENIED' && !useAdmin) {
        if (isBrowser) {
          const message = err.error?.message || 'ไม่มีสิทธิ์เข้าถึงหน้านี้';
          notify.warning('ไม่มีสิทธิ์เข้าถึง', message);
          // sync user (เผื่อ permission ถูกแก้ฝั่ง back-end) แล้วค่อย redirect
          authSvc.refreshFromToken();
          const fallback = getDefaultOwnerRoute(authSvc.currentUser());
          if (router.url.startsWith('/owner')) {
            router.navigateByUrl(fallback);
          }
        }
        // กลืน error ไม่ให้ caller ต้องจัดการ — UI เปลี่ยนหน้าแล้ว
        return EMPTY;
      }

      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      // ── Admin API 401 handling ─────────────────────────────
      if (useAdmin) {
        // ข้าม refresh สำหรับ admin refresh/signin เอง (ป้องกัน loop)
        const isAdminAuthMgmt = req.url.includes('/api/auth/admin/refresh') ||
                                req.url.includes('/api/auth/admin/signin');
        if (isAdminAuthMgmt) return throwError(() => err);

        // SSR: ไม่มี cookie → ไม่ต้อง refresh/signout (กันการ navigate ไป /admin/account
        // ทำให้ SSR render login page แล้ว flash ตอน client hydrate)
        if (!isBrowser) return throwError(() => err);

        return adminAuthSvc.refreshAdminToken().pipe(
          switchMap(res => {
            if (res.accessToken) {
              const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${res.accessToken}` } });
              return next(retryReq);
            }
            adminAuthSvc.adminSignout();
            return throwError(() => err);
          }),
          catchError(() => {
            adminAuthSvc.adminSignout();
            return throwError(() => err);
          }),
        );
      }

      // ── Committee API 401 handling ─────────────────────────
      // session token หมดอายุ/ใช้ไม่ได้ → ล้าง + เด้งไปหน้า claim (ไม่มี refresh)
      if (useCommittee) {
        if (isBrowser) {
          committeeToken.clear();
          router.navigate(['/committee/claim'], { queryParams: { expired: 1 } });
        }
        return throwError(() => err);
      }

      // ── Owner/Bidder API 401 handling (เดิม) ────────────────
      const isAuthMgmt = (req.method === 'PUT' && req.url.includes('/api/auth/refresh')) ||
                         (req.method === 'DELETE' && req.url.includes('/api/auth/signout'));
      if (isAuthMgmt) return throwError(() => err);

      // SSR: ข้าม refresh/signout (กัน navigate ตอน server render)
      if (!isBrowser) return throwError(() => err);

      return authSvc.refreshAccessToken().pipe(
        switchMap(res => {
          if (res.accessToken) {
            const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${res.accessToken}` } });
            return next(retryReq);
          }
          authSvc.signout();
          return throwError(() => err);
        }),
        catchError(() => {
          authSvc.signout();
          return throwError(() => err);
        }),
      );
    }),
  );
};
